import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, input, signal } from "@angular/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgxMatErrorsModule } from "ngx-mat-errors";
import { ActivatedRoute, Router } from "@angular/router";
import { SnackbarMsgService } from "app/shared";
import { ListApplicationsComponent } from "../list/list-applications.component";
import { ApplicationsApiService } from "../applications.api.service";
import { ApplicationsStateService } from "../applications.state.service";
import { tap } from "rxjs";
import { AuthMode } from "../../shared";
import { IApplicationForCreation } from "../@shared";
import { FuseAlertComponent, FuseAlertType } from "@fuse/components/alert";
import { MatCheckboxModule } from "@angular/material/checkbox";

@Component({
    selector: 'application-detail',
    templateUrl: './application-detail.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatTooltipModule,
        NgxMatErrorsModule,
        FuseAlertComponent,
        MatCheckboxModule
    ]
})
export class ApplicationDetailComponent implements OnInit {
    private readonly _applicationsListComp = inject(ListApplicationsComponent);

    applicationId = input<string>();

    isCreatingApplication = computed(() => !this.applicationId() || this.applicationId() === 'add');

    applicationForm: FormGroup;

    loading = this._applicationsState.loadingState;

    authModes = Object.values(AuthMode);

    showWebhookSecret = signal<boolean>(false);

    webhookSecret = signal<string>('');

    alert = signal<{ type: FuseAlertType; message: string }>({
        type: 'success',
        message: ''
    });

    constructor(
        private readonly _applicationsApiService: ApplicationsApiService,
        private readonly _fb: FormBuilder,
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _snackbarMsgService: SnackbarMsgService,
        private readonly _applicationsState: ApplicationsStateService
    ) {
        effect(() => {
            this.applicationId();

            if (!this.isCreatingApplication()) {
                this._getApplication(this.applicationId());
            } else {
                this.applicationForm?.reset({
                    authMode: AuthMode.Legacy
                });
            }
        }, { allowSignalWrites: true });
    }

    ngOnInit(): void {
        this._applicationsListComp.matDrawer().open();
        this.applicationForm = this._createApplicationForm();
    }

    onCancelClick() {
        this._router.navigate(['..'], { relativeTo: this._activatedRoute });
    }

    onSubmitClick() {
        if (this.isCreatingApplication()) {
            this._createApplication();
            return;
        }
        
        this._editApplication();
    }

    private _createApplicationForm() {
        return this._fb.group({
            name: new FormControl('', [Validators.required, Validators.maxLength(100)]),
            redirectUri: new FormControl('', [Validators.required, Validators.maxLength(200)]),
            userEditUri: new FormControl('', [Validators.maxLength(200)]),
            secretKey: new FormControl('', this.isCreatingApplication() ? [Validators.required, Validators.minLength(8)] : []),
            authMode: new FormControl(AuthMode.Legacy, [Validators.required]),
            webhookEnabled: new FormControl(false),
            webhookUrl: new FormControl(''),
            webhookSecret: new FormControl('')
        });
    }

    private _createApplication() {
        if (this.applicationForm.invalid) {
            this.applicationForm.markAllAsTouched();
            return;
        }

        const application = this.applicationForm.value as IApplicationForCreation;

        this._applicationsState.createApplication(application).pipe(
            tap((app) => {
                this._router.navigate(['..', app.id], { relativeTo: this._activatedRoute });
                
                if (app.webhookGeneratedSecret) {
                    this.alert.set({
                        type: 'success',
                        message: `Chave secreta do webhook gerada: ${app.webhookGeneratedSecret}`
                    });
                    this.showWebhookSecret.set(true);
                }
                
                this._snackbarMsgService.success('Sucesso!', 'Aplicação criada com sucesso');
            })
        ).subscribe();
    }

    private _editApplication() {
        if (this.applicationForm.invalid) {
            this.applicationForm.markAllAsTouched();
            return;
        }

        const application = this.applicationForm.value as IApplicationForCreation;

        this._applicationsState.editApplication(this.applicationId(), application).pipe(
            tap(() => {
                this._snackbarMsgService.success('Sucesso!', 'Aplicação atualizada com sucesso');
            })
        ).subscribe();
    }

    private _getApplication(id: string) {
        this._applicationsApiService.getApplication(id).pipe(
            tap((app) => {
                const secretKeyControl = this.applicationForm.get('secretKey');
                secretKeyControl.clearValidators();
                secretKeyControl.setValidators([]);
                secretKeyControl.updateValueAndValidity();

                this.applicationForm.patchValue({
                    name: app.name,
                    redirectUri: app.redirectUri,
                    userEditUri: app.userEditUri,
                    secretKey: app.secretKey,
                    authMode: app.authMode,
                    webhookEnabled: app.webhookEnabled,
                    webhookUrl: app.webhookUrl,
                    webhookSecret: ""
                });
                this.applicationForm.markAsPristine();
                this.applicationForm.markAsUntouched();

                // Atualiza validadores do webhookUrl baseado no webhookEnabled
                this._updateWebhookValidators(app.webhookEnabled);
            })
        ).subscribe();
    }

    private _updateWebhookValidators(enabled: boolean) {
        const webhookUrlControl = this.applicationForm.get('webhookUrl');
        
        if (enabled) {
            webhookUrlControl.setValidators([Validators.required, Validators.maxLength(200)]);
        } else {
            webhookUrlControl.clearValidators();
        }
        
        webhookUrlControl.updateValueAndValidity();
    }

    onWebhookEnabledChange(event: any) {
        this._updateWebhookValidators(event.checked);
    }

    closeDrawer() {
        return this._applicationsListComp.matDrawer().close();
    }

    private _generateSecretKey(): string {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let secretKey = '';
        
        // Gera uma chave de 256 caracteres
        for (let i = 0; i < 256; i++) {
            const randomIndex = Math.floor(Math.random() * characters.length);
            secretKey += characters[randomIndex];
        }
        
        return secretKey;
    }

    onGenerateSecretKeyClick() {
        this.applicationForm.get('secretKey').setValue(this._generateSecretKey());
    }

    onGenerateWebhookSecretClick() {
        this.applicationForm.get('webhookSecret').setValue(this._generateSecretKey());
    }
} 